import { Ref, useEffect, useState } from "react";
import useDarkMode from "use-dark-mode";
import Map, {
  LngLatBoundsLike,
  MapLayerMouseEvent,
  MapRef,
} from "react-map-gl";
import { useSidebar } from "@/components/ui/sidebar";

interface MapboxProps {
  longitude?: number;
  latitude?: number;
  bbox?: LngLatBoundsLike;
  bboxPadding?: number;
  zoom?: number;
  pitch?: number;
  bearing?: number;
  onZoom: (zoom: number) => void;
  style: React.CSSProperties;
  children?: React.ReactNode;
  transformRequest?: (url: string, resource: string) => any;
  mapRef: Ref<MapRef>;
  onClick?: (event: MapLayerMouseEvent) => void;
  interactiveLayerIds?: string[];
  attributionControl: boolean;
  minZoom?: number;
  maxZoom?: number;
  interactive?: boolean;
  mapStyle?: string;
  boundingBox?: LngLatBoundsLike;
  basemap?: string;
}

/**
 * Examples of using Native Mapbox Layers: https://visgl.github.io/react-map-gl/docs/get-started/adding-custom-data#native-mapbox-layers
 * Examples of using Custom Overlays: https://visgl.github.io/react-map-gl/docs/get-started/adding-custom-data#custom-overlays
 * Other Examples: https://visgl.github.io/react-map-gl/examples
 * @param props
 * @returns
 */
export const DEFAULT_MAP_VIEW = {
  latitude: -35.32186,
  longitude: 149.14683,
  zoom: 7,
} as const;

const MAP_STYLES = {
  IMAGERY: "mapbox://styles/geoscape-psma/clu7jjdp602em01pt0m1agkrj",
  BLANK_DARK: "mapbox://styles/geoscape-psma/cmfw1pxpy000y01smhzzvg6yc",
  BLANK_LIGHT: "mapbox://styles/geoscape-psma/cmfw1w7z3001001sm4urec653",
  DEFAULT_DARK: "mapbox://styles/geoscape-psma/clpt56v2100ey01r84bav7gn1",
  DEFAULT_LIGHT: "mapbox://styles/geoscape-psma/clpt55jcl00f201px05cd3bf1",
} as const;

export const Basemap = (props: MapboxProps) => {
  const { value: isDark } = useDarkMode(false);

  const getMapStyle = (): string => {
    if (props.basemap === "Imagery") {
      return MAP_STYLES.IMAGERY;
    }

    if (props.mapStyle === "BLANK") {
      return isDark ? MAP_STYLES.BLANK_DARK : MAP_STYLES.BLANK_LIGHT;
    }

    if (props.mapStyle) {
      return props.mapStyle;
    }

    return isDark ? MAP_STYLES.DEFAULT_DARK : MAP_STYLES.DEFAULT_LIGHT;
  };

  // If lat/lon/zoom are passed it will initialise the map to there
  const [viewState, setViewState] = useState(
    props.latitude && props.longitude && props.zoom
      ? {
          longitude: props.longitude,
          latitude: props.latitude,
          zoom: props.zoom,
          ...(props.pitch !== undefined && { pitch: props.pitch }),
          ...(props.bearing !== undefined && { bearing: props.bearing }),
        }
      : {}
  );

  // Otherwise if bbox is passed we will initialise to the bbox
  const initialViewState = props.bbox
    ? {
        bounds: props.bbox,
        fitBoundsOptions: { padding: props.bboxPadding ?? 20 },
      }
    : undefined;

  // When the sidebar opens/closes, repeatedly resize the map during the
  // CSS transition so the canvas tracks the container width.
  const { state: sidebarState } = useSidebar();
  useEffect(() => {
    const mapRef = props.mapRef as { current: MapRef | null } | null;
    const map = mapRef?.current?.getMap();
    if (!map) return;
    const start = performance.now();
    const TRANSITION_MS = 250;
    let rafId: number;
    const tick = (now: number) => {
      map.resize();
      if (now - start < TRANSITION_MS) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [sidebarState, props.mapRef]);

  return (
    <Map
      {...viewState}
      initialViewState={initialViewState}
      interactive={props.interactive ?? true}
      interactiveLayerIds={props.interactiveLayerIds}
      onMove={(evt) => setViewState(evt.viewState)}
      onZoom={(e) => props.onZoom(e.viewState.zoom)}
      ref={props.mapRef}
      onClick={props.onClick}
      // mapboxAccessToken={`pk.eyJ1IjoiZ2Vvc2NhcGUtcHNtYSIsImEiOiJjbHB0NHoxOWkwOGI5MnZvamNmYXNsbXduIn0.g7bYF-gyWHGoee-3xkRFDw`}
      mapboxAccessToken={`${import.meta.env.VITE_MAPBOXGL_TOKEN}`}
      mapLib={import("mapbox-gl")}
      transformRequest={props.transformRequest}
      style={props.style}
      styleDiffing
      mapStyle={getMapStyle()}
      attributionControl={props.attributionControl}
      minZoom={props.minZoom ? props.minZoom : 0}
      maxZoom={props.maxZoom ? props.maxZoom : 24}
      onRender={(event) => event.target.resize()}
      maxBounds={props.boundingBox}
    >
      {props.children}
    </Map>
  );
};
